import { useState } from 'react';
import type { CursorPage, Partner } from '@dru/types';
import { Alert, Button, Field, Input, Select, TextSkeleton } from '@dru/ui';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';

type Row = { id: string; note: string | null; partner: { id: string; code: string; name: string; type: string; city: string | null } };

/**
 * Which labs run this company's examinations (owner, 4 Nov 2026: Dr Ultimate
 * decides). Once any are assigned, HR can route and run campaigns only to
 * these; with none, the whole network is open, as before.
 */
export function CompanyLabAssignments({ tenantId }: { tenantId: string }) {
  const rows = useQuery(() => api.get<{ items: Row[] }>(`/admin/partner-assignments/labs?tenantId=${tenantId}`), [tenantId]);
  const partners = useQuery(() => api.get<CursorPage<Partner>>('/partners?limit=100&status=ACTIVE'), []);
  const [partnerId, setPartnerId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const items = rows.data?.items ?? [];
  const assigned = new Set(items.map((r) => r.partner.id));

  const add = async () => {
    setBusy(true); setError(null);
    try {
      await api.post('/admin/partner-assignments/labs', { tenantId, partnerId, ...(note.trim() ? { note: note.trim() } : {}) }, crypto.randomUUID());
      setPartnerId(''); setNote(''); rows.reload();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  const end = async (id: string) => {
    setError(null);
    try { await api.del(`/admin/partner-assignments/labs/${tenantId}/${id}`); rows.reload(); }
    catch (e) { setError(errorMessage(e)); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h4 text-neutral-900">Labs for examinations</h2>
        <p className="text-small text-neutral-600">
          Where this company's employees go for their checkups. HR can send examinations only to these labs.
        </p>
      </div>
      {error && <Alert>{error}</Alert>}
      {rows.error ? <Alert>{rows.error}</Alert> : rows.loading ? <TextSkeleton lines={3} /> : items.length === 0 ? (
        <Alert tone="info">No lab assigned yet, so HR can choose any lab in the network. Assign one or more to limit it.</Alert>
      ) : (
        <ul className="divide-y divide-neutral-200 rounded-card border border-neutral-200">
          {items.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0">
                <div className="font-medium text-neutral-900">{r.partner.name}</div>
                <div className="text-caption text-neutral-600">{[r.partner.code, r.partner.city, r.note].filter(Boolean).join(' · ')}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => end(r.id)} className="max-md:min-h-10">Remove</Button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <Field label="Assign a lab">
          {(p) => (
            <Select {...p} value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
              <option value="">Choose a lab…</option>
              {(partners.data?.items ?? []).filter((x) => !assigned.has(x.id)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </Select>
          )}
        </Field>
        <Field label="Note">{(p) => <Input {...p} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Covers Pune plant" />}</Field>
        <Button disabled={!partnerId} loading={busy} onClick={add} className="max-md:w-full">Assign</Button>
      </div>
    </div>
  );
}
